/**
 * Copyright Higharc Inc. 2019
 *
 * GLSL code with utilities for dealing with grids of cube maps.
 * @module
 */

export const cubeMapGridHelpers = `
// Return a vector with cube map face index in the z coordinate, and integer u and v coordinates inside the
// cube map face in the x and y coordinates.
// Cube map face enumeration order is: +x, -x, +y, -y, +z, -z
ivec3 cubeFaceAndCubeXYFromDirectionVector( vec3 direction, int cubeResolution ) {
	int faceIndex = 0;
	vec2 cubeFaceUV = vec2(0.0);
	vec3 absDirection = abs( direction );
	if ( absDirection.x > absDirection.y && absDirection.x > absDirection.z ) {
		cubeFaceUV = direction.zy / absDirection.x;
		if ( direction.x < 0.0 ) {
			faceIndex = 1;
		} else {
			cubeFaceUV.x = -cubeFaceUV.x;
		}
	} else {
		if ( absDirection.y > absDirection.z ) {
			cubeFaceUV = direction.xz / absDirection.y;
			if ( direction.y > 0.0 ) {
				faceIndex = 2;
				cubeFaceUV.y = -cubeFaceUV.y;
			} else {
				faceIndex = 3;
			}
		} else {
			cubeFaceUV = direction.xy / absDirection.z;
			if ( direction.z > 0.0 ) {
				faceIndex = 4;
			} else {
				faceIndex = 5;
				cubeFaceUV.x = -cubeFaceUV.x;
			}
		}
	}
	float halfCubeResolution = 0.5 * float( cubeResolution );
	ivec2 intUV = ivec2( clamp( floor( ( cubeFaceUV + 1.0 ) * halfCubeResolution ), 0.0, float( cubeResolution - 1 ) ) );
	return ivec3( intUV, faceIndex );
}

vec2 getInterleavedCubeMapGridPixelCoordinates( vec2 gridCoordinatesXY, int gridCoordinatesZ, ivec3 cubeFaceCoordsAndFaceIndex, ivec3 probeCount, int cubeResolution ) {

	return vec2(
			( cubeFaceCoordsAndFaceIndex.z * probeCount.z + gridCoordinatesZ ) * probeCount.x,
			( cubeFaceCoordsAndFaceIndex.y * cubeResolution + cubeFaceCoordsAndFaceIndex.x ) * probeCount.y
		)
		+ gridCoordinatesXY + 0.5;
}

vec4 sampleInterleavedCubeMapAtlas( sampler2D probeData, vec3 worldCoordinates, vec3 worldNormal, float aoFactor, ivec3 probeCount, mat4 worldToGridMatrix, int cubeResolution, vec3 direction ) {
	vec4 worldPointHomogenous = vec4( worldCoordinates, 1.0 );
	vec3 gridCoordinates = ( worldToGridMatrix * worldPointHomogenous ).xyz;
	vec4 normalHomogenous = vec4( worldNormal, 0.0 );
	gridCoordinates += normalize( ( worldToGridMatrix * normalHomogenous ).xyz ) * probeGridNormalOffsetMultiplier * aoFactor;
	vec2 gridCoordinatesFloorCeilZ = max( min( vec2( floor( gridCoordinates.z ), ceil( gridCoordinates.z ) ), float( probeCount.z ) - 1.0 ), 0.0 );
	gridCoordinates = clampCoordinatesToGrid( gridCoordinates, probeCount );

	float maxZWeight = gridCoordinates.z - gridCoordinatesFloorCeilZ.x;
	vec2 pixelSizeInTexCoords = 1.0 / vec2( probeCount.x * probeCount.z * 6, probeCount.y * cubeResolution * cubeResolution );

	ivec3 cubeFaceCoordsAndFaceIndex = cubeFaceAndCubeXYFromDirectionVector( direction, cubeResolution );

	vec2 texCoordinates = getInterleavedCubeMapGridPixelCoordinates( gridCoordinates.xy, int( gridCoordinatesFloorCeilZ.x ), cubeFaceCoordsAndFaceIndex, probeCount, cubeResolution ) * pixelSizeInTexCoords;
	vec4 sampleMinZ = texture2D( probeData, texCoordinates );
	texCoordinates = getInterleavedCubeMapGridPixelCoordinates( gridCoordinates.xy, int( gridCoordinatesFloorCeilZ.y ), cubeFaceCoordsAndFaceIndex, probeCount, cubeResolution ) * pixelSizeInTexCoords;
	vec4 sampleMaxZ = texture2D( probeData, texCoordinates );
	return mix( sampleMinZ, sampleMaxZ, maxZWeight );
}
`;
